import { apiFetch } from '../../lib/api';
import type { SubgraphApiResponse, MappedSubgraph } from './mapSubgraphResponse';
import { mapSubgraphResponse } from './mapSubgraphResponse';
import type { ExplorerQueryState } from './explorerQueryState';

// PKGV.B2 (#796) — multi-anchor/anchor-less/filtered fetch against B1's
// GET /projects/:projectId/pkg/subgraph. Broader surface than A2's
// single-anchor fetchSubgraph.ts (multi-anchor, node/edge type filters,
// no fixed depth) — S2 adjudication: don't touch A2's unmerged worktree,
// duplicate this fetch layer for now, extract a shared mapper once A2
// merges (tracked in #1884).
//
// IMPORTANT (§8 adversarial item 2 — filter->param fidelity): B1's
// nodeTypes/edgeTypes query params treat an EMPTY token list identically
// to an OMITTED one (both mean "no filter", i.e. show everything) — its
// own Zod transform collapses a zero-length array to `undefined`. That
// means there is no way to ask the server for "zero node/edge types" —
// sending an empty selection would silently return the UNFILTERED
// graph, not an empty one. So:
//   * nodeTypes: an empty selection is handled ENTIRELY client-side —
//     we skip the fetch and return an empty result, never send an empty
//     nodeTypes param and hope the server does the right thing.
//   * edgeTypes: an empty selection also can't be expressed to the
//     server (same collapse-to-undefined behavior) — we still fetch
//     nodes normally (edgeTypes doesn't gate node visibility) but drop
//     ALL edges from the mapped result before it reaches <PKGCanvas />,
//     so "0 edge types checked" reliably renders 0 edges.
export async function fetchExplorerSubgraph(
  projectId: string,
  query: ExplorerQueryState,
): Promise<MappedSubgraph> {
  const nodeTypesSelected = query.nodeTypes !== undefined;
  if (nodeTypesSelected && query.nodeTypes!.length === 0) {
    return { nodes: [], edges: [], truncated: false };
  }

  const params = new URLSearchParams();
  if (query.anchorIds?.length) params.set('anchorIds', query.anchorIds.join(','));
  if (query.nodeTypes?.length) params.set('nodeTypes', query.nodeTypes.join(','));
  if (query.edgeTypes?.length) params.set('edgeTypes', query.edgeTypes.join(','));

  const raw = await apiFetch<SubgraphApiResponse>(
    `/projects/${projectId}/pkg/subgraph?${params.toString()}`,
  );
  const mapped = mapSubgraphResponse(raw);

  const edgeTypesSelected = query.edgeTypes !== undefined;
  if (edgeTypesSelected && query.edgeTypes!.length === 0) {
    return { ...mapped, edges: [] };
  }

  return mapped;
}
